import { type ApolloClient, gql } from '@apollo/client/core';
import * as VPError from '../../VPError';
import * as WebauthnCredential from './WebauthnCredential';
import { ACCOUNT_AGNOSTIC } from '../accountContext';

// Two of these run before there is a session at all — the sign-in challenge and the sign-in
// itself — so they are marked account-agnostic; there is no account to scope them to yet.
//
// The other four are credential management on the `/passkeys` settings page, which is reached long
// after the account has resolved. They are deliberately left account-scoped: exempting them would
// strip a header they carry today, for no gain. The connection store would at worst hold them for
// the moment it takes the account to arrive, and on that page it has already arrived.

const WEBAUTHN_CREDENTIAL_FIELDS = gql`
  fragment WebauthnCredentialFields on WebauthnCredential {
    id
    nickname
    createdAt
    lastUsedAt
    transports
  }
`;

const WEBAUTHN_CREDENTIALS_QUERY = gql`
  query WebauthnCredentials {
    me {
      webauthnCredentials {
        ...WebauthnCredentialFields
      }
    }
  }
  ${WEBAUTHN_CREDENTIAL_FIELDS}
`;

export const fetchCredentials = async (client: ApolloClient<unknown>): Promise<WebauthnCredential.WebauthnCredential[]> => {
  const { data } = await client.query({
    query: WEBAUTHN_CREDENTIALS_QUERY,
    fetchPolicy: 'network-only',
  });

  return data.me.webauthnCredentials.map(WebauthnCredential.fromGQL);
};

const GENERATE_AUTHENTICATION_OPTIONS_MUTATION = gql`
  mutation GenerateWebauthnAuthenticationOptions {
    generateWebauthnAuthenticationOptions(input: {}) {
      challengeId
      optionsJson
      errors {
        message
        path
      }
    }
  }
`;

export type AuthenticationOptionsResult = {
  challengeId?: string;
  optionsJson?: string;
  errors?: VPError.VPError[];
};

export const generateAuthenticationOptions = async (client: ApolloClient<unknown>): Promise<AuthenticationOptionsResult> => {
  const { data } = await client.mutate({
    mutation: GENERATE_AUTHENTICATION_OPTIONS_MUTATION,
    context: ACCOUNT_AGNOSTIC,
  });

  return {
    challengeId: data.generateWebauthnAuthenticationOptions.challengeId,
    optionsJson: data.generateWebauthnAuthenticationOptions.optionsJson,
    errors: VPError.fromGqlCollection(data.generateWebauthnAuthenticationOptions.errors),
  };
};

const SIGNIN_WITH_PASSKEY_MUTATION = gql`
  mutation SigninWithPasskey($challengeId: String!, $assertionResponseJson: String!) {
    signinWithPasskey(input: { challengeId: $challengeId, assertionResponseJson: $assertionResponseJson }) {
      authToken
      returnTo
      errors {
        message
        path
      }
    }
  }
`;

export type SigninWithPasskeyInput = {
  challengeId: string;
  assertionResponseJson: string;
};

export type SigninWithPasskeyResult = {
  authToken?: string;
  returnTo?: string;
  errors?: VPError.VPError[];
};

export const signinWithPasskey = async (client: ApolloClient<unknown>, input: SigninWithPasskeyInput): Promise<SigninWithPasskeyResult> => {
  const { data } = await client.mutate({
    mutation: SIGNIN_WITH_PASSKEY_MUTATION,
    variables: input,
    context: ACCOUNT_AGNOSTIC,
  });

  return {
    authToken: data.signinWithPasskey.authToken,
    returnTo: data.signinWithPasskey.returnTo,
    errors: VPError.fromGqlCollection(data.signinWithPasskey.errors),
  };
};

const GENERATE_REGISTRATION_OPTIONS_MUTATION = gql`
  mutation GenerateWebauthnRegistrationOptions {
    generateWebauthnRegistrationOptions(input: {}) {
      challengeId
      optionsJson
      errors {
        message
        path
      }
    }
  }
`;

export type RegistrationOptionsResult = {
  challengeId?: string;
  optionsJson?: string;
  errors?: VPError.VPError[];
};

export const generateRegistrationOptions = async (client: ApolloClient<unknown>): Promise<RegistrationOptionsResult> => {
  const { data } = await client.mutate({
    mutation: GENERATE_REGISTRATION_OPTIONS_MUTATION,
  });

  return {
    challengeId: data.generateWebauthnRegistrationOptions.challengeId,
    optionsJson: data.generateWebauthnRegistrationOptions.optionsJson,
    errors: VPError.fromGqlCollection(data.generateWebauthnRegistrationOptions.errors),
  };
};

const REGISTER_CREDENTIAL_MUTATION = gql`
  mutation RegisterWebauthnCredential($challengeId: String!, $attestationResponseJson: String!, $nickname: String) {
    registerWebauthnCredential(input: { challengeId: $challengeId, attestationResponseJson: $attestationResponseJson, nickname: $nickname }) {
      credential {
        ...WebauthnCredentialFields
      }
      errors {
        message
        path
      }
    }
  }
  ${WEBAUTHN_CREDENTIAL_FIELDS}
`;

export type RegisterCredentialInput = {
  challengeId: string;
  attestationResponseJson: string;
  nickname?: string;
};

export type RegisterCredentialResult = {
  credential?: WebauthnCredential.WebauthnCredential;
  errors?: VPError.VPError[];
};

export const registerCredential = async (client: ApolloClient<unknown>, input: RegisterCredentialInput): Promise<RegisterCredentialResult> => {
  const { data } = await client.mutate({
    mutation: REGISTER_CREDENTIAL_MUTATION,
    variables: input,
  });

  const gqlCredential = data.registerWebauthnCredential.credential;

  return {
    credential: gqlCredential ? WebauthnCredential.fromGQL(gqlCredential) : undefined,
    errors: VPError.fromGqlCollection(data.registerWebauthnCredential.errors),
  };
};

const DELETE_CREDENTIAL_MUTATION = gql`
  mutation DeleteWebauthnCredential($id: Int!) {
    deleteWebauthnCredential(input: { id: $id }) {
      success
      errors {
        message
        path
      }
    }
  }
`;

export type DeleteCredentialResult = {
  success: boolean;
  errors?: VPError.VPError[];
};

export const deleteCredential = async (client: ApolloClient<unknown>, id: string): Promise<DeleteCredentialResult> => {
  const { data } = await client.mutate({
    mutation: DELETE_CREDENTIAL_MUTATION,
    variables: { id: parseInt(id, 10) },
  });

  return {
    success: data.deleteWebauthnCredential.success,
    errors: VPError.fromGqlCollection(data.deleteWebauthnCredential.errors),
  };
};
